/**
 * The tick.
 *
 * Order matters and is deliberate:
 *   commands → spatial → navigation → economy → arrivals → targeting →
 *   swings → projectiles → towers → powers → movement → morale →
 *   companies → cleanup → phase.
 *
 * Every system reads a world that the previous system has finished with. The
 * spatial hash is built once, before anything queries it, and is allowed to go
 * stale during the movement pass — a unit's neighbours as of the start of the
 * tick is exactly the right answer for a fixed-step simulation.
 */

import { TICK_DT } from './constants.ts';
import { Phase } from './types.ts';
import { applyCommands } from './apply.ts';
import type { Command } from './commands.ts';
import { LogTone, pushLog } from './events.ts';
import { statsOf, stepUnwedge, type World } from './world.ts';
import { maybeRebuildFlows } from './systems/pathing.ts';
import { stepSettlement } from './systems/settlement.ts';
import { stepDeadfall } from './systems/deadfall.ts';
import { beginSiege, planNextWave, stepAgeResearch, stepDirector } from './systems/director.ts';
import { stepCalendar } from './systems/calendar.ts';
import { stepAttacks, stepMorale, stepTargeting } from './systems/combat.ts';
import { stepProjectiles } from './systems/projectiles.ts';
import { recount, stepStructureCleanup, stepTowers } from './systems/towers.ts';
import { stepPowers } from './systems/powers.ts';
import { stepHero } from './systems/hero.ts';
import { stepMovement } from './systems/movement.ts';
import { stepCompanies } from './systems/companies.ts';
import { stepGarrison } from './systems/garrison.ts';
import { stepBattle } from './battle.ts';
import { stepRealm } from './campaign/turn.ts';
import { relicsAvailable } from '../data/relics.ts';
import { pickWeighted } from '../core/rng.ts';

/**
 * One tick of a whole campaign: the realm, and the city standing in it.
 *
 * A campaign has always been two live `World`s — `main.ts` says so in as many
 * words — and until now only one of them was ever ticking. Whichever the player
 * was looking at ran; the other was parked. That is what made the strategic
 * layer turn-based in the first place: a realm nobody is watching cannot be
 * allowed to run if it is the only thing running.
 *
 * Both run now, every tick, from the same `dt`. Walk out of your city and the
 * masons keep working; stand in your city and the hosts keep marching. This
 * function is the only place that ordering is decided, and it is fixed rather
 * than "whichever is on screen first", so which screen the player happens to be
 * on cannot change what happens.
 *
 * **The realm goes first.** A host that arrives at the gates this tick is on the
 * board before the city ticks the frame in which the player could be told about
 * it — never the other way round, which would show a warning one tick after the
 * thing it was warning about had already landed.
 *
 * Commands go to whichever world the player is actually in. There is no way to
 * order a world you are not looking at, and there should not be.
 */
export function stepGame(
  realm: World | null | undefined,
  city: World | null | undefined,
  cmds: { realm?: readonly Command[]; city?: readonly Command[] } = {},
  /**
   * The player's **other** towns.
   *
   * A crown may hold several settlements now — see `campaign/holdworld.ts` —
   * and every one of them is a real world that keeps working while nobody is
   * looking at it, which is the same promise this function was written to make
   * about the capital. `city` is whichever one is on screen; these are the
   * rest, and they take no commands, because there is no way to order a world
   * you are not standing in and there should not be.
   *
   * **The order is the caller's and must be stable.** `main.ts` passes them in
   * ascending province index. Stepping them in the order a `Map` happens to
   * iterate would make the tick depend on the order the player entered their
   * towns, which is exactly the class of thing a replay cannot reproduce.
   *
   * Costed before it was written: a lived-in settlement is 0.20 ms a tick
   * (`npm run towncost`), so four towns is 0.8 ms of a 16.7 ms frame. Memory,
   * not time, is what bounds this — 27.5 MiB of typed array each.
   */
  others: readonly World[] = EMPTY_WORLDS,
): void {
  if (realm) stepWorld(realm, cmds.realm ?? EMPTY);
  if (city && city !== realm) stepWorld(city, cmds.city ?? EMPTY);
  for (const w of others) {
    if (w === realm || w === city) continue;
    stepWorld(w, EMPTY);
  }
}

const EMPTY_WORLDS: readonly World[] = [];

export function stepWorld(w: World, cmds: readonly Command[] = EMPTY): void {
  w.events.length = 0;

  if (w.gameOver) {
    // Keep applying debug commands so the console still works on the death screen.
    applyCommands(w, cmds);
    w.tick++;
    return;
  }

  applyCommands(w, cmds);

  /**
   * Time stops for the Chronicle, and it stops *here*, in the simulation.
   *
   * The tempting version of this is a flag in the UI that skips `stepWorld`
   * while the modal is up. That desynchronises replays the moment two players
   * take a different number of frames to read the same paragraph: the command
   * log says "answered on tick 4210" and the world it lands in depends on how
   * long somebody stared at it. Freezing on `w.pendingEvent` instead — which is
   * hashed and saved — means the pause is part of the state, identical in every
   * replay, and a headless bot experiences exactly the pause a player does.
   *
   * The tick counter still advances. It is the clock the command log is keyed
   * to, not the simulation's own time, and stopping it would make a script that
   * fires at tick N wait forever.
   *
   * Note this sits *after* `applyCommands`: the answer arrives and the world
   * resumes on the same tick, so nobody pays a frame for deciding.
   */
  if (w.pendingEvent) {
    w.tick++;
    return;
  }

  /**
   * The one pause that is not a Chronicle question: the player is away fighting
   * a battle by hand.
   *
   * "There should be no turns, the game keeps going real time, and the
   * construction and resources only pause if player is in a battle." This is
   * that sentence, and it is *state* for exactly the reason `pendingEvent` is:
   * a flag in the frame loop that skipped `stepWorld` would make the length of
   * the pause a fact about how long somebody took to win a fight on their
   * machine, and two replays of the same command log would part company. Hashed
   * and saved, the hold is part of what "this world" means, so a headless bot
   * experiences precisely the pause a player does.
   *
   * It is set through `Cmd.SetBattleHold` like everything else. `main.ts` holds
   * the parked worlds on the way into a battle and releases them on the way out.
   *
   * A battle world can never hold itself — the guard below is belt and braces
   * against a hold command arriving in the wrong world and freezing the fight
   * the player is standing in.
   */
  if (w.battleHold && !w.battle) {
    w.tick++;
    return;
  }

  w.spatial.rebuild(w.units);
  maybeRebuildFlows(w);
  w.paths.beginTick();

  // Battle mode replaces the settlement and the director wholesale; everything
  // below this point — targeting, swings, morale, powers — is shared.
  if (w.battle) {
    stepBattle(w, TICK_DT);
  } else {
    /**
     * The realm and the settlement are no longer alternatives.
     *
     * This used to be `else if (w.campaign)`, and that `else if` *was* the
     * turn-based-ness: a world with a campaign on it ran no clock at all and
     * waited for a button. Now every non-battle world runs the Long Ledger, and
     * a world with a campaign on it runs the realm off that ledger as well.
     *
     * The settlement systems are still skipped for a campaign world, and that
     * is not the old fork wearing a hat. A campaign `World` is the *bare realm*
     * — `createCampaignWorld` never calls `initRun`, so it has no Hearth, no
     * villagers, no buildings and nothing for a director to plan against.
     * Running `stepSettlement` on it would not be "letting the city live", it
     * would be simulating an empty field. The player's city is a second, real
     * `World`, and it is stepped *alongside* this one by `stepGame` above —
     * which is where "the city and the realm live at the same time" actually
     * happens.
     *
     * Order within the tick, and it is deliberate: the settlement first, then
     * the ledger, then the realm. The realm reads `calendarDay` to decide
     * whether the month has turned, so the ledger has to have moved first or the
     * council would always sit one tick late; and the settlement's producers
     * read the season, so they see the same season all the way through a tick.
     */
    if (!w.campaign) {
      stepSettlement(w, TICK_DT);
      // After the settlement, so it reads the stores the producers just moved,
      // and here rather than inside `stepSettlement` only because it needs the
      // market out of `apply.ts` and `apply.ts` imports `settlement.ts`.
      stepDeadfall(w, TICK_DT);
      stepDirector(w, TICK_DT);
      // Through sieges as well as build phases: the climb does not stop because
      // you are busy, and that is the whole of its cost.
      stepAgeResearch(w, TICK_DT);
    }
    // The year turns for the map as well as for the town. A wave does not
    // happen outside the calendar, and neither does a harvest or a march.
    stepCalendar(w, TICK_DT);
    if (w.campaign) stepRealm(w);
  }

  /**
   * Deployment is a truce.
   *
   * The simulation deliberately keeps running while the player arranges their
   * line — that live preview of formations settling into a dragged shape is
   * worth more than a hard pause. But *only* the settling. Nothing else was
   * gated, so both armies were quietly fighting a battle that had not started:
   * `stepBattle` returns early while deploying, which freezes `elapsed`,
   * resolve, the grace window and the outcome, while targeting and swings ran
   * on regardless. A player who spent a minute on their deployment began the
   * battle already mauled, and an army annihilated before the horns could never
   * register a result at all.
   */
  const deploying = w.battle?.deploying === true;

  if (!deploying) {
    stepTargeting(w);
    stepAttacks(w, TICK_DT);
    stepProjectiles(w, TICK_DT);
    stepTowers(w, TICK_DT);
    stepPowers(w, TICK_DT);
    stepHero(w, TICK_DT);
  }

  stepMovement(w, TICK_DT);
  // The safety net under the eviction at the stamp. Bounded, deterministic, and
  // after movement so a unit shoved into stone this tick is out again next.
  stepUnwedge(w);
  if (!deploying) stepMorale(w, TICK_DT);
  stepCompanies(w, TICK_DT);
  // After the companies, because it works off a roster the compaction pass has
  // just finished with: replacements and the men who fell off a broken section
  // are both looking for a stone to stand on, and both have to be live members
  // before they can be given one.
  stepGarrison(w);

  stepStructureCleanup(w);
  recount(w);
  stepPhase(w, TICK_DT);

  w.tick++;
}

const EMPTY: Command[] = [];

export function stepPhase(w: World, dt: number): void {
  // A battle has no build timer, no waves and no spoils. Neither has a campaign.
  if (w.battle || w.campaign) {
    if (w.gameOver && w.phase !== Phase.GameOver) w.phase = Phase.GameOver;
    return;
  }
  /**
   * A capital has no clock.
   *
   * Its sieges arrive from the strategic map, so there is nothing for a build
   * timer to count down to and nothing for the director to plan. Left running,
   * the timer would sound the horn on a town whose besiegers are four provinces
   * away and still marching.
   */
  if (w.config.homeCity) {
    if (w.phase === Phase.Build) w.phaseT = Number.POSITIVE_INFINITY;
  } else if (w.phase === Phase.Build) {
    if (w.incomingComposition.length === 0) planNextWave(w);
    w.phaseT -= dt;
    if (w.phaseT <= 0) {
      // The wave-end Will grant lives in `endSiege`, where a wave actually
      // ends. Granting it here as well paid it twice per cycle — and only for
      // players who let the build timer run out, since `Cmd.StartWave` calls
      // `beginSiege` directly and never passed through this branch. Starting a
      // wave early should not cost you Will.
      beginSiege(w);
    }
  }

  // Spoils: a relic offer after every tenth wave.
  if (w.phase === Phase.Build && w.wave > 0 && w.wave % 10 === 0 && w.victoryWave < w.wave) {
    w.victoryWave = w.wave;
    offerRelics(w);
  }

  if (w.gameOver && w.phase !== Phase.GameOver) {
    w.phase = Phase.GameOver;
    const stats = statsOf(w);
    pushLog(
      w.events,
      `The run ends at wave ${w.wave}. ${stats.kills} of them. ${stats.losses} of yours.`,
      LogTone.Dire,
    );
  }
}

function offerRelics(w: World): void {
  const pool = relicsAvailable(w.relics, w.wave);
  if (pool.length === 0) return;
  const weights = pool.map((r) => (r.rarity === 1 ? 3 : r.rarity === 2 ? 2 : 1));
  const chosen: string[] = [];
  const local = pool.slice();
  const localW = weights.slice();
  for (let n = 0; n < 3 && local.length > 0; n++) {
    const pick = pickWeighted(w.rng, local, localW);
    const idx = local.indexOf(pick);
    local.splice(idx, 1);
    localW.splice(idx, 1);
    chosen.push(pick.id);
  }
  w.relicOffer = chosen;
  pushLog(w.events, 'Spoils. Choose what the realm becomes.', LogTone.Lore);
}

/** Convenience for tests and the headless harness. */
export function runTicks(w: World, count: number, cmdsAt?: (tick: number) => Command[] | undefined): void {
  for (let i = 0; i < count; i++) {
    const cmds = cmdsAt?.(w.tick);
    stepWorld(w, cmds ?? EMPTY);
    if (w.gameOver) break;
  }
}
